<template>
  <div>
    <gmap-map :center="center" :zoom="zoom" map-type-id="terrain" id="admin-map" :style="sidebarRight" ref="mapRef">
      <gmap-marker
        v-if="driver.location.latitude && driver.location.longitude" :key="index"
        v-for="(driver, index) in filteredDrivers"
        :position="{ lat: parseFloat(driver.location.latitude), lng: parseFloat(driver.location.longitude) }"
        :clickable="true"
        :draggable="false"
        :icon="{ url: iconUrl(driver, 'driver'), scaledSize: iconScale('driver') }"
        @click="handleDriverClick(driver, 'driver')"
      >
        <gmap-info-window
          v-if="driver.owner && !isClient"
          :opened="currentInfo.type === 'driver' && currentInfo.id === driver.id"
          @closeclick="closeInfoWindow()"
        >
          <strong>{{ driver.name }}</strong><br>
          <br/>
          <template v-for="truck in driver.trucks">
            <strong>Truck: </strong>{{ truck.registration }} - {{ truck.company }}<br/>
          </template>

          <template v-if="driver.load && driver.load.status === 'in_progress'">
            <br/>
            <strong>Job:</strong>
            {{ driver.load.job }} - {{ driver.load.from }} to {{ driver.load.to }}
            <br/>
          </template>

          <strong>Last Location: </strong>{{ driver.location.age }}<br/>
          <strong>Available Since: </strong>{{ driver.age }}
          <br/>
        </gmap-info-window>
      </gmap-marker>
      <gmap-marker
        :key="index"
        v-for="(job, index) in filteredJobs"
        :position="{ lat: parseFloat(job.location_from.latitude), lng: parseFloat(job.location_from.longitude) }"
        :clickable="true"
        :draggable="false"
        :icon="{ url: iconUrl(job, 'job'), scaledSize: iconScale('job') }"
        @click="handleJobClick(job)"
      >
      </gmap-marker>

      <gmap-marker
        v-if="selectedJob"
        :position="{ lat: parseFloat(selectedJob.location_to.latitude), lng: parseFloat(selectedJob.location_to.longitude) }"
        :clickable="false"
        :draggable="false"
        :icon="{ url: iconUrl(selectedJob, 'job-to'), scaledSize: iconScale('job') }"
      >
      </gmap-marker>
      <gmap-polyline
        v-if="selectedJob"
        :draggable="false"
        :editable="false"
        :path="polylinePath"
        :options="{ strokeWeight: 2, strokeOpacity: 0.5, deepWatch: true }"
      >
      </gmap-polyline>
    </gmap-map>
    <filters :style="sidebarWidth" v-model="filters" :drivers="drivers" :jobs="jobs"></filters>
    <job-details v-model="selectedJob"></job-details>
    <truck-details v-model="selectedDriver"></truck-details>
    <button type="button" class="btn-map-legend" data-toggle="modal" data-target=".modal-map-legend">
      <i class="far fa-map-signs"></i>
    </button>
    <button @click="handleToggleSidebar" type="button" class="btn-map-filters active" :style="filterPosition">
      <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
    </button>
    <legend-modal></legend-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { forEach, intersection } from 'lodash'
import LegendModal from './LegendModal'
import Filters from './Filters'
import JobDetails from './JobDetails'
import TruckDetails from './TruckDetails'

const SIDEBAR_WIDTH = 300

export default {
  data () {
    return {
      width: SIDEBAR_WIDTH,
      getDetailsInterval: null,
      currentInfo: {
        type: null,
        id: null
      },
      center: { lat: -23.450052, lng: 144.439667 },
      zoom: 6,
      filters: {
        show: {
          jobs: true,
          requests: true,
          activeTrucks: true,
          inactiveTrucks: false
        },
        jobs: [],
        trucks: [],
        carriers: []
      },
      selectedJob: null,
      selectedDriver: null
    }
  },

  components: {
    LegendModal,
    Filters,
    JobDetails,
    TruckDetails
  },

  computed: {
    ...mapState('map', ['drivers', 'jobs']),
    ...mapState('app', ['loading']),

    sidebarWidth () {
      return {
        width: `${this.width}px`
      }
    },

    sidebarRight () {
      return {
        right: `${this.width}px`
      }
    },

    filterPosition () {
      return {
        right: `${this.width + 10}px`
      }
    },

    isClient () {
      if (this.$user.exists) {
        return this.$user.isClient
      }
      return false
    },

    google () {
      return window.google
    },

    /**
     * Only show rates if user is not a carrier
     */
    userIsNotCarrier () {
      if (this.$user.exists) {
        return !this.$user.isCarrier
      }

      return true
    },

    filteredDrivers () {
      return this.drivers.filter((driver) => {
        if (driver.active) {
          if (!this.filters.show.activeTrucks) {
            return false
          }
        } else {
          if (!this.filters.show.inactiveTrucks) {
            return false
          }
        }

        if (this.filters.trucks.length) {
          const trucks = driver.trucks.map((truck) => {
            return truck.registration
          })
          if (!intersection(trucks, this.filters.trucks).length) {
            return false
          }
        }

        if (this.filters.carriers.length) {
          const carriers = driver.carriers.map((carrier) => {
            return carrier.company
          })
          if (!intersection(carriers, this.filters.carriers).length) {
            return false
          }
        }

        return true
      })
    },

    filteredJobs () {
      return this.jobs
        .filter(job => job.location_from.latitude && job.location_from.longitude)
        .filter(job => {
          if (job.pending) {
            if (!this.filters.show.requests) {
              return false
            }
          } else {
            if (!this.filters.show.jobs) {
              return false
            }
          }

          if (this.filters.jobs.length) {
            if (!this.filters.jobs.includes(job.number) && !this.filters.jobs.includes(job.client.company)) {
              return false
            }
          }

          return true
        })
    },

    polylinePath () {
      if (this.selectedJob) {
        let google = window.google.maps
        const line = {
          from: new google.LatLng(this.selectedJob.location_from.latitude, this.selectedJob.location_from.longitude),
          to: new google.LatLng(this.selectedJob.location_to.latitude, this.selectedJob.location_to.longitude)
        }

        return [line.from, line.to]
      }
      return []
    }
  },

  watch: {
    /**
     * Watch the application loading, once loaded, we'll resize the map.
     *
     * @return void
     */
    loading (loading) {
      if (loading) return

      this.$nextTick(() => (this.$emit('ready')))
    }
  },

  created () {
    this.resetDetails()
    this.getDetails()

    this.getDetailsInterval = setInterval(() => {
      this.getDetails()
    }, 15000)
  },

  mounted () {
    this.$on('ready', this.draw)
    // this.$on('refresh', this.draw)
  },

  beforeDestroy () {
    clearInterval(this.getDetailsInterval)
    this.$off('ready', this.draw)
    // this.$off('refresh', this.draw)
  },

  methods: {
    ...mapActions('map', ['getDetails', 'resetDetails']),

    handleToggleSidebar () {
      this.width = this.width === SIDEBAR_WIDTH ? 0 : SIDEBAR_WIDTH
    },

    draw () {
      if (this.drivers.length || this.jobs.length) {
        let google = window.google.maps

        let bounds = new google.LatLngBounds()

        forEach(this.drivers, driver => {
          bounds.extend({ lat: parseFloat(driver.location.latitude), lng: parseFloat(driver.location.longitude) })
        })

        forEach(this.jobs, job => {
          bounds.extend({ lat: parseFloat(job.location_from.latitude), lng: parseFloat(job.location_from.longitude) })
        })

        this.center = bounds.getCenter()

        this.$refs.mapRef.fitBounds(bounds)

        if (this.zoom < 6) {
          this.zoom = 6
        }

        if (this.$route.query.driver) {
          this.currentInfo = {
            type: 'driver',
            id: parseInt(this.$route.query.driver)
          }
        }
      }
    },

    handleJobClick (job) {
      this.selectedJob = job
    },

    handleDriverClick (driver) {
      this.selectedDriver = driver
    },

    handleMarkerClick (details, type) {
      this.currentInfo = {
        type: type,
        id: details.id
      }
    },

    closeInfoWindow () {
      this.currentInfo = {
        type: null,
        id: null
      }
    },

    iconUrl (details, type) {
      if (type === 'job') {
        if (details.pending) {
          return '/static/icons/Asset 10.png'
        }

        return '/static/icons/Asset 4.png'
      } else if (type === 'driver') {
        if (details.owner || details.client) {
          return '/static/icons/Asset 2.png'
        }
        return '/static/icons/Asset 9.png'
      } else if (type === 'job-to') {
        return '/static/icons/Asset 6.png'
      }
    },

    iconScale (type) {
      if (type === 'driver') {
        return new this.google.maps.Size(35, 50)
      }
      return new this.google.maps.Size(22, 32)
    }
  }
}
</script>



// WEBPACK FOOTER //
// Map.vue?28041565